/**
 * The browser's id source, bound to `IdPort`.
 *
 * `crypto.randomUUID` is the right answer and is used whenever it is there. The
 * complication is that it is **secure-context only**: on `http://` served from
 * anything other than `localhost` the `crypto` object still exists and still has
 * `getRandomValues`, but `randomUUID` is simply absent. That is not an exotic
 * case — it is a phone opening the app from a LAN address during development, or
 * any origin behind a proxy that terminates TLS elsewhere — and a `TypeError`
 * thrown from a click handler that was trying to create a goal is a poor way to
 * discover it.
 *
 * So this adapter degrades twice, and both steps still produce a well-formed
 * version-4 UUID:
 *
 *  1. `crypto.randomUUID()`.
 *  2. Sixteen bytes from `crypto.getRandomValues`, formatted as a v4 UUID.
 *  3. `Math.random()`, same formatting.
 *
 * Step 3 is not cryptographically random, and that is acceptable *here and only
 * here*: these ids name records in the user's own local database. They are never
 * a capability, a token, or a secret — nothing in the product grants access on
 * the basis of guessing one — so the requirement is collision resistance among
 * one person's records, which 122 random bits satisfies even from a weak source.
 * Anything in this app that ever does need unguessable bytes must take them from
 * `crypto.getRandomValues` directly and fail closed when it is missing, rather
 * than reaching for this port.
 */

import type { IdPort } from "@ff/domain/ports";
import { idPortFromSource } from "@ff/domain/ports";

/**
 * The parts of `crypto` this adapter uses, and nothing more. Structural so a
 * test can present a browser that has only some of them — which is the whole
 * behaviour being adapted — without standing up a `Crypto`.
 */
export interface IdEntropySource {
  /** Absent outside a secure context, and on engines predating the method. */
  readonly randomUUID?: (() => string) | undefined;
  /** Present in insecure contexts too; absent only on very old engines. */
  readonly getRandomValues?: ((array: Uint8Array) => Uint8Array) | undefined;
}

/**
 * The globals, resolved once per construction so a test can substitute them.
 *
 * Both methods are bound: `crypto.getRandomValues` throws `Illegal invocation`
 * when called with the wrong receiver, and pulling a method off an object into a
 * structural field is exactly how that happens.
 */
export function browserIdEntropySource(): IdEntropySource {
  const source = globalThis.crypto as Crypto | undefined;
  if (source === undefined) return {};
  return {
    randomUUID:
      typeof source.randomUUID === "function" ? () => source.randomUUID() : undefined,
    getRandomValues:
      typeof source.getRandomValues === "function"
        ? (array) => source.getRandomValues(array)
        : undefined,
  };
}

const UUID_BYTE_LENGTH = 16;

/** Byte → two lowercase hex digits, precomputed; this runs per id, per byte. */
const HEX_BYTE: readonly string[] = Array.from({ length: 256 }, (_unused, value) =>
  value.toString(16).padStart(2, "0"),
);

/**
 * Formats sixteen random bytes as a version-4 UUID.
 *
 * The version and variant bits are overwritten rather than hoped for: a string
 * that merely looks like a UUID but declares version 7 or an invalid variant is
 * a value some other layer is entitled to reject, and every id here has to be
 * indistinguishable from one `randomUUID` produced.
 */
function formatUuidV4(bytes: Uint8Array): string {
  // `noUncheckedIndexedAccess` is right that a caller could pass a short array;
  // every caller in this file allocates exactly `UUID_BYTE_LENGTH`.
  const byte = (index: number): number => bytes[index] ?? 0;
  const digits: string[] = [];
  for (let index = 0; index < UUID_BYTE_LENGTH; index += 1) {
    let value = byte(index);
    if (index === 6) value = (value & 0x0f) | 0x40;
    if (index === 8) value = (value & 0x3f) | 0x80;
    digits.push(HEX_BYTE[value] ?? "00");
  }
  return (
    digits.slice(0, 4).join("") +
    "-" +
    digits.slice(4, 6).join("") +
    "-" +
    digits.slice(6, 8).join("") +
    "-" +
    digits.slice(8, 10).join("") +
    "-" +
    digits.slice(10, 16).join("")
  );
}

function randomBytes(source: IdEntropySource): Uint8Array {
  const bytes = new Uint8Array(UUID_BYTE_LENGTH);
  const fill = source.getRandomValues;
  if (fill !== undefined) {
    fill(bytes);
    return bytes;
  }
  for (let index = 0; index < UUID_BYTE_LENGTH; index += 1) {
    // `Math.random() * 256` never reaches 256, so no clamp is needed.
    bytes[index] = Math.floor(Math.random() * 256);
  }
  return bytes;
}

/**
 * Whether this adapter is using `crypto.randomUUID`.
 *
 * Reported rather than silently degraded so a diagnostic surface can say which
 * source is live; the ids themselves are interchangeable either way.
 */
export function usesNativeRandomUuid(source: IdEntropySource): boolean {
  return source.randomUUID !== undefined;
}

/**
 * `IdPort` over the browser's entropy.
 *
 * Every member mints from the same UUID source: the branding, not the string,
 * is what separates a goal id from an entry id, and inventing per-kind formats
 * here would put presentation into a value that goes straight into storage keys.
 */
export function browserIds(source: IdEntropySource = browserIdEntropySource()): IdPort {
  const native = source.randomUUID;
  const mintRaw =
    native !== undefined ? () => native() : () => formatUuidV4(randomBytes(source));
  return idPortFromSource(mintRaw);
}
